// Pick which protocol the user should see first, given the time of day
// and what they've already completed today.
//
// Ordering rule (priority order; first match wins per protocol):
//   1. Current-window protocol, if incomplete — always primary. The
//      time band determines what's "right now" and the user's
//      eyeline goes there first. Anything else is secondary by
//      construction.
//   2. Missed: a protocol whose ideal window has already passed
//      today and is still incomplete. Renders BELOW the current-
//      window card with a rust "Missed" badge so the user can pick
//      it up if they want without it eclipsing what's actually
//      relevant right now.
//   3. Upcoming: incomplete protocols whose window is later today,
//      ordered by how soon they arrive.
//   4. Completed today: pushed to the bottom, muted, "Completed
//      today" pill at the render layer.
//
// Note on the previous version: an earlier rev of this file made
// missed protocols tier-0 (above current). That meant a user
// opening the app at 8:51pm with Morning Anchor never started saw
// Morning Anchor as the *primary* card — which is confusing and
// wrong. Evening Wind-Down is what 8:51pm wants. The reorder here
// reflects feedback from that surface.
//
// Completion semantics: a protocol is "complete today" when there's
// a completion row for every step *with today's calendar date*. A
// protocol completed yesterday counts as zero today — the caller
// (Protocol.jsx) keys completion lookups by ``localDateKey()`` which
// already enforces this.

export const MORNING_PROTOCOL_ID = "morning-anchor";
export const MIDDAY_PROTOCOL_ID = "midday-clarity-reset";
export const EVENING_PROTOCOL_ID = "evening-wind-down";

// Time-of-day bands aligned with the protocol cadence:
//   •  5–11   morning   → Morning Anchor
//   • 11–17   afternoon → Midday Clarity Reset
//   • 17–24   evening   → Evening Wind-Down
//   •  0–5    rest      → "Rest well." Nothing surfaces as active;
//                         the app is asleep along with the user.
//
// ``timeWindow`` returns ``null`` for the rest band so callers can
// branch on it ("show the rest-well copy") rather than guessing a
// protocol the user shouldn't be poked toward at 3am.
export function timeWindow(hour = new Date().getHours()) {
  if (hour >= 5  && hour < 11) return "morning";
  if (hour >= 11 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 24) return "evening";
  return null;   // 0–5 — rest window, nothing active
}

// Stable mapping from protocol id → its ideal time window. Used to
// classify each protocol relative to "now" (current / missed /
// upcoming) without re-deriving the relationship from time bands.
const PROTOCOL_WINDOW = {
  [MORNING_PROTOCOL_ID]: "morning",
  [MIDDAY_PROTOCOL_ID]:  "afternoon",
  [EVENING_PROTOCOL_ID]: "evening",
};

// Strict ordering of windows across the day. Indices drive the
// "ahead of / at / behind now" classification used by the queue.
const WINDOW_ORDER = ["morning", "afternoon", "evening"];

// Completion is strict equality: every rendered step today must have a
// matching completion row. Earlier revs used ``>=`` which combined with
// the summary endpoint's static-config ``step_count`` (which does NOT
// include today's interleaved library steps) silently flipped a 4-of-5
// session to "complete" — the user saw 5 steps, ticked 4, and the queue
// card lit up gold. The render layer now passes ``totalsByProtocol``
// (today's true rendered count from ``/protocols/{id}/today``) when it
// can; the fallback to ``protocol.step_count`` keeps legacy callers
// working but undercounts on protocols with variable injections.
export function isComplete(protocol, completedCount, totalsByProtocol = null) {
  if (!protocol) return false;
  const stepCount = totalsByProtocol?.[protocol.id] ?? protocol?.step_count ?? 0;
  const done = completedCount?.[protocol.id] ?? 0;
  return stepCount > 0 && done === stepCount;
}

// True iff this protocol's window has already passed today and the
// user hasn't completed it. Drives the rust "Missed" badge.
//
// We deliberately *don't* mark the current-window protocol as
// missed — a user who opens the app at 10:55 with Morning Anchor
// undone should see it as "current" right up until 11:00, then
// flip cleanly to "missed" at 11:00:00. Same logic at every band
// boundary.
//
// During the 0–5 rest window every incomplete out-of-window
// protocol is technically behind us *for today*. We still don't
// flag them as missed: at 3am the right copy is "rest", not "you
// missed three things." The render layer treats the rest window
// as a separate state and won't show missed badges anyway.
export function isMissed(protocol, completedCount, now = new Date(), totalsByProtocol = null) {
  if (!protocol) return false;
  // Completion wins over time-of-day: a protocol the user finished
  // retroactively (e.g. ran Morning Anchor at 8pm) must read as
  // COMPLETED, never MISSED. The order here is load-bearing — flipping
  // it would re-introduce the bug where a freshly-completed protocol
  // kept its rust badge for the rest of the day.
  if (isComplete(protocol, completedCount, totalsByProtocol)) return false;
  const protoWindow = PROTOCOL_WINDOW[protocol.id];
  if (!protoWindow) return false;
  const nowWindow = timeWindow(now.getHours());
  if (nowWindow === null) return false;   // rest window — no missed badges
  const protoIdx = WINDOW_ORDER.indexOf(protoWindow);
  const nowIdx = WINDOW_ORDER.indexOf(nowWindow);
  return protoIdx !== -1 && nowIdx !== -1 && protoIdx < nowIdx;
}

// Internal: rank a single protocol against "now" so the queue can
// sort by a single integer key. Lower = surfaces first.
//
//   0  current window (incomplete) — the primary card.
//   1  missed (incomplete, window already passed) — secondary, below
//      the current card. Rust "Missed" badge at the render layer.
//   2  upcoming (incomplete, window later today) — ordered by how
//      soon the window arrives.
//   3  completed today — pushed to the bottom regardless of window.
//
// Note: this differs from a prior rev where missed sat at tier 0.
// The right behaviour for the F&F surface is "primary = right now",
// with everything else stacked behind it.
function _queueRank(proto, completedCount, now, totalsByProtocol = null) {
  const done = isComplete(proto, completedCount, totalsByProtocol);
  if (done) return { tier: 3, sub: 0 };

  const protoWindow = PROTOCOL_WINDOW[proto.id];
  const nowWindow = timeWindow(now.getHours());
  const protoIdx = WINDOW_ORDER.indexOf(protoWindow);
  const nowIdx = nowWindow === null ? -1 : WINDOW_ORDER.indexOf(nowWindow);

  // Rest window (0–5) — every protocol is technically out-of-window.
  // Sub-rank upcoming (= morning ahead of midday ahead of evening)
  // so that if the rest-window UI does fall through to a queue,
  // Morning Anchor sits up top — the user's first action of the
  // new day. No tier-1 "missed" pile here; see isMissed().
  if (nowIdx === -1) {
    return { tier: 2, sub: protoIdx === -1 ? 99 : protoIdx };
  }

  if (protoIdx === -1) return { tier: 2, sub: 99 };
  if (protoIdx === nowIdx) return { tier: 0, sub: 0 };       // current
  if (protoIdx <  nowIdx) return { tier: 1, sub: nowIdx - protoIdx };  // missed
  return { tier: 2, sub: protoIdx - nowIdx };                // upcoming
}

// After this hour, missed protocols (tier 1) get filtered from the
// queue entirely. Showing "Morning Anchor — MISSED" at 11pm is noise,
// not signal — the user can't act on it tonight, and it eclipses the
// wind-down they actually should be running. The cutoff is local
// hour; matches the boundary between the rest-window and the back
// half of the evening band.
const MISSED_HIDE_AFTER_HOUR = 21;

// Order the "Up Next" queue per the priority rules above. Used by
// Protocol.jsx so a finished protocol drops out of the user's eyeline
// once they're done with it, and a missed protocol surfaces below
// (not above) what's actually relevant right now.
//
// Late-evening pruning: from 21:00 local onward, drop missed (tier 1)
// protocols entirely. Completed and current/upcoming entries still
// render. The render layer doesn't need to know — once we filter
// here, the "Missed" badge surface naturally has nothing to show.
export function orderedQueue(protocols, completedCount = {}, activeId = null, now = new Date(), totalsByProtocol = null) {
  if (!Array.isArray(protocols) || protocols.length === 0) return [];
  const hideMissed = now.getHours() >= MISSED_HIDE_AFTER_HOUR;
  return protocols
    .filter(p => p.id !== activeId)
    .map(p => ({ p, rank: _queueRank(p, completedCount, now, totalsByProtocol) }))
    .filter(({ rank }) => !(hideMissed && rank.tier === 1))
    .sort((a, b) => {
      if (a.rank.tier !== b.rank.tier) return a.rank.tier - b.rank.tier;
      return a.rank.sub - b.rank.sub;
    })
    .map(({ p }) => p);
}

// Fixed chronological ordinal per protocol. Drives the full-day list
// rendered below the active card: Morning → Midday → Evening, regardless
// of which one is "active" right now. Unknown ids sink to the bottom.
const CHRONOLOGICAL_ORDER = {
  [MORNING_PROTOCOL_ID]: 0,
  [MIDDAY_PROTOCOL_ID]:  1,
  [EVENING_PROTOCOL_ID]: 2,
};

// Return the protocols sorted in fixed chronological order. Unlike
// orderedQueue (which is reactive to "now" and completion state and
// hides missed entries late-evening), this is a stable Morning →
// Midday → Evening list that always shows every protocol the user
// has for the day. The render layer attaches a status badge per card
// via protocolStatus().
export function chronologicalProtocols(protocols) {
  if (!Array.isArray(protocols)) return [];
  return [...protocols].sort((a, b) => {
    const ai = CHRONOLOGICAL_ORDER[a?.id] ?? 99;
    const bi = CHRONOLOGICAL_ORDER[b?.id] ?? 99;
    return ai - bi;
  });
}

// Status badge for a card in the chronological daily list. Returns
// one of:
//   • "current"   — protocol's time window matches "now"; no badge
//                   (it's the active hero card at the top).
//   • "completed" — every step done today; wins over a past window so
//                   a finished Morning at 9pm reads COMPLETED, not
//                   MISSED.
//   • "missed"    — window has passed today and the protocol is still
//                   incomplete. Cards stay tappable so the user can
//                   open and complete them out-of-window.
//   • "upcoming"  — window is later today, OR we're in the rest band
//                   (0–5am) where the daily list shouldn't even render
//                   but the helper still has to return *something*.
export function protocolStatus(protocol, completedCount, now = new Date(), totalsByProtocol = null) {
  if (!protocol) return "upcoming";
  // Completion check runs first by design — a protocol the user
  // finished is "completed" regardless of which time band we're in.
  // Without this priority a Morning Anchor done at 8pm would read as
  // MISSED because its window is behind us; we'd be telling the user
  // they missed something they actually just did.
  if (isComplete(protocol, completedCount, totalsByProtocol)) return "completed";
  const protoWindow = PROTOCOL_WINDOW[protocol.id];
  const nowWindow = timeWindow(now.getHours());
  if (!protoWindow || nowWindow === null) return "upcoming";
  const pIdx = WINDOW_ORDER.indexOf(protoWindow);
  const nIdx = WINDOW_ORDER.indexOf(nowWindow);
  if (pIdx === nIdx) return "current";
  if (pIdx < nIdx) return "missed";
  return "upcoming";
}

// Rich status used by the queue / detail / home cards. Combines the
// completion count + true-today total with the time-of-day status so a
// single call gives the renderer everything it needs to pick a badge or
// a progress bar.
//
//   completed:    every rendered step today is done (gold "Completed").
//   missed:       window has passed AND not all steps done. Renders the
//                 rust "Missed · X of Y" pill so the user sees how far
//                 they got, not just that they fell off.
//   in_progress:  window is current AND at least one (but not all) step
//                 is done. Renders the progress bar with "X of Y", no
//                 pill — the bar IS the status.
//   not_started:  window is current AND zero steps done. No badge —
//                 normal active state.
//   upcoming:     window is later today (or rest band). Renders the
//                 muted "Upcoming" pill so the day's structure stays
//                 visible.
//
// Returns ``{ state, done, total }`` so the render layer never has to
// look up step counts a second time.
export function protocolProgress(protocol, completedCount, now = new Date(), totalsByProtocol = null) {
  if (!protocol) {
    return { state: "upcoming", done: 0, total: 0 };
  }
  const total = totalsByProtocol?.[protocol.id] ?? protocol?.step_count ?? 0;
  const done = completedCount?.[protocol.id] ?? 0;
  if (total > 0 && done === total) {
    return { state: "completed", done, total };
  }
  const status = protocolStatus(protocol, completedCount, now, totalsByProtocol);
  if (status === "current") {
    return { state: done > 0 ? "in_progress" : "not_started", done, total };
  }
  if (status === "missed") {
    return { state: "missed", done, total };
  }
  return { state: "upcoming", done, total };
}

// Strict time-of-day primary pick. Returns:
//   • the current-window protocol id, if it's incomplete today;
//   • else the next incomplete protocol per queue order (missed
//     before upcoming);
//   • ``null`` between midnight and 5am (rest window) — the caller
//     renders "Rest well." copy and skips the protocol surface
//     entirely;
//   • the first protocol's id as a last-resort fallback when every
//     protocol is complete today (the Protocol tab still needs a
//     card to render so the user can review what they finished).
export function pickActiveProtocolId(protocols, completedCount = {}, now = new Date(), totalsByProtocol = null) {
  if (!Array.isArray(protocols) || protocols.length === 0) return null;
  const nowWindow = timeWindow(now.getHours());
  if (nowWindow === null) return null;

  const byId = new Map(protocols.map(p => [p.id, p]));

  // 1. Strict primary: the current-window protocol, if incomplete.
  //    This is the rule the "8:51pm shows Morning Anchor" bug
  //    violated — we never want a missed earlier protocol to
  //    eclipse the band the user is actually in right now.
  const currentId = Object.entries(PROTOCOL_WINDOW)
    .find(([, w]) => w === nowWindow)?.[0];
  const currentProto = currentId ? byId.get(currentId) : null;
  if (currentProto && !isComplete(currentProto, completedCount, totalsByProtocol)) {
    return currentId;
  }

  // 2. Current is complete (or missing). Walk the queue for the
  //    next incomplete protocol — missed ahead of upcoming per
  //    _queueRank, but always *behind* the current-window match
  //    above when one exists.
  const ordered = orderedQueue(protocols, completedCount, null, now, totalsByProtocol);
  const nextIncomplete = ordered.find(p => !isComplete(p, completedCount, totalsByProtocol));
  if (nextIncomplete) return nextIncomplete.id;

  // 3. Everything is complete. Surface the current-window protocol
  //    if we have it; otherwise the first protocol in the array.
  if (currentProto) return currentId;
  return protocols[0].id;
}
